import { useSendResult } from '../../hooks/useSendResult';
import { cn } from '../ui/cn';
import { SendProgressIndicator } from './SendProgressIndicator';

/**
 * Counts-only send result (PRO-0210 / UC-EP6-002). Shown after an immediate send:
 * how many messages have sent / are sending / are queued / failed — NEVER a
 * recipient name, address or body (SEC-8). Polls via `useSendResult`.
 */
export interface SendResultPanelProps {
  readonly sendId: string;
}

const statClassName =
  'inline-block min-w-[5.5rem] rounded-md border border-border bg-surface px-3 py-2 text-center';

export function SendResultPanel({ sendId }: SendResultPanelProps): JSX.Element {
  const { counts, state, progress, excluded, undeliverable, smsOptOutSuppressed, noPreferredMobile, busy, error } = useSendResult(sendId);

  return (
    <div aria-labelledby="send-result-heading" className="mt-3">
      <h4 id="send-result-heading" className="mb-2 mt-0 font-semibold">
        Delivery so far
      </h4>
      {error ? (
        <p role="alert" className="m-0 text-danger">
          {error}
        </p>
      ) : counts ? (
        <>
          {state !== null && progress !== null ? (
            <SendProgressIndicator
              state={state}
              progress={progress}
              counts={{
                queued: counts.QUEUED,
                sending: counts.SENDING,
                sent: counts.SENT,
                failed: counts.FAILED,
              }}
            />
          ) : null}
          <dl aria-live="polite" className="m-0 flex flex-wrap gap-2">
            <div className={statClassName}>
              <dt className="text-xs text-text-secondary">Sent</dt>
              <dd className="m-0 font-bold">{counts.SENT}</dd>
            </div>
            <div className={statClassName}>
              <dt className="text-xs text-text-secondary">Sending</dt>
              <dd className="m-0 font-bold">{counts.SENDING}</dd>
            </div>
            <div className={statClassName}>
              <dt className="text-xs text-text-secondary">Queued</dt>
              <dd className="m-0 font-bold">{counts.QUEUED}</dd>
            </div>
            <div className={statClassName}>
              <dt className="text-xs text-text-secondary">Failed</dt>
              <dd className="m-0 font-bold">{counts.FAILED}</dd>
            </div>
            {excluded !== null ? (
              <div className={statClassName}>
                <dt className="text-xs text-text-secondary">Excluded</dt>
                <dd className="m-0 font-bold">{excluded}</dd>
              </div>
            ) : null}
            {undeliverable !== null ? (
              <div className={cn(statClassName)}>
                <dt className="text-xs text-text-secondary">Undeliverable</dt>
                <dd className="m-0 font-bold">{undeliverable}</dd>
              </div>
            ) : null}
            {smsOptOutSuppressed !== null && smsOptOutSuppressed > 0 ? (
              <div className={statClassName}>
                <dt className="text-xs text-text-secondary">Opted out of SMS</dt>
                <dd className="m-0 font-bold">{smsOptOutSuppressed}</dd>
              </div>
            ) : null}
            {noPreferredMobile !== null && noPreferredMobile > 0 ? (
              <div className={statClassName}>
                <dt className="text-xs text-text-secondary">No preferred mobile</dt>
                <dd className="m-0 font-bold">{noPreferredMobile}</dd>
              </div>
            ) : null}
          </dl>
        </>
      ) : (
        <p aria-live="polite" className="m-0">
          {busy ? 'Checking delivery…' : 'No delivery information yet.'}
        </p>
      )}
    </div>
  );
}
